import { Placeholder } from 'utils/constants';
import { getSubmissionSections } from 'utils/gradingNavigation';
import { downloadCSV, getSubmissionGradedBy, getSubmissionGradingTime, stripHTML } from 'utils/helper';
import { getQuizOverview } from 'utils/quizOverview';
import { QuizRubricType } from 'utils/quizRubric';
import {
    getQuizCriterionMaxPoints,
    getQuizCriterionScore,
    getQuizGradeSnapshot,
    getQuizQuestionScore,
    getQuizRubricScore,
} from 'utils/quizScoring';
import { getQuizSubmissionStatus, QuizSubmissionStatus } from 'utils/quizSubmissionList';
import { isUnscoredRubric } from 'utils/rubricScoring';

const columns = [
    ['student-id', 'Student ID'],
    ['student-name', 'Student Name'],
    ['sections', 'Section(s)'],
    ['total-points', 'Total Quiz Points Possible'],
    ['overall-average', 'Class Average (Final Quiz Grade)'],
    ['suggested-overall', 'AI Suggested Final Quiz Grade'],
    ['selected-overall', 'Instructor Selected Final Quiz Grade'],
    ['question', 'Question'],
    ['question-points', 'Maximum Points Possible (Question)'],
    ['question-average', 'Class Average (Question)'],
    ['suggested-question', 'AI Suggested Grade (Question)'],
    ['selected-question', 'Instructor Selected Grade (Question)'],
    ['criteria', 'Question Criteria'],
    ['criterion-points', 'Maximum Points Possible (Criteria)'],
    ['criterion-average', 'Class Average (Criteria)'],
    ['suggested-criterion', 'AI Suggested Grade (Criteria)'],
    ['selected-criterion', 'Instructor Selected Grade (Criteria)'],
    ['generated-feedback', 'Generated Feedback (Question)'],
    ['final-feedback', 'Final Feedback (Question)'],
    ['grading-time', 'Grading Time (mm:ss) (Quiz)'],
    ['graded-by', 'Graded By (Quiz)'],
];

const holisticColumns = [
    ...columns.slice(0, 7),
    ['holistic.criterion', 'Criterion'],
    ['holistic.criterion-points', 'Maximum Points Possible'],
    ['holistic.criterion-average', 'Class Average'],
    ['holistic.suggested-grade', 'AI Suggested Grade'],
    ['holistic.suggested-rating', 'AI Suggestioned Rating'],
    ['holistic.selected-grade', 'Instructor Selected Grade'],
    ['holistic.selected-rating', 'Instructor Selected Rating'],
    ...columns.slice(-2),
    ['holistic.generated-feedback', 'First Pass Feedback'],
    ['holistic.final-feedback', 'Final Feedback '],
];

const cell = (value) => (value == null ? '' : typeof value === 'number' ? Number(value.toFixed(3)) : value);
const sum = (values) =>
    values.length && values.every((value) => value != null) ? values.reduce((total, value) => total + value, 0) : null;
const average = (values) => {
    const scores = values.filter((value) => value != null);
    return scores.length ? sum(scores) / scores.length : null;
};
const confirmed = (submission) => getQuizSubmissionStatus(submission) === QuizSubmissionStatus.REVIEWED;
const maxPoints = getQuizCriterionMaxPoints;
const criterionScore = (submission, criterion, suggested, scored) =>
    scored ? getQuizCriterionScore(submission, criterion, { suggested }) : null;
const questionScore = (submission, question, suggested, scored) =>
    scored ? getQuizQuestionScore(submission, question, { suggested }) : null;

const text = (value) => stripHTML(value).trim();
const formatFeedback = (feedback, context, includeGeneral, mappedCriterionIds, quiz, selections, t) => {
    if (!feedback || feedback.active === false) return '';
    if (typeof feedback === 'string') return includeGeneral ? text(feedback) : '';
    const parts = [];
    if (includeGeneral) parts.push(text(feedback.commentary || feedback.feedback || feedback.text || ''));
    if (feedback.feedback_type === 'templated') {
        context.criteria.forEach((criterion) => {
            if (!criterion) return;
            const template = quiz.ai_feedback_settings?.templated?.[criterion.id]?.[selections?.[criterion.id]];
            if (template) parts.push([text(criterion.title), text(template)].filter(Boolean).join('\n'));
        });
    }
    const criterionIds = new Set(context.criterionIds.map(String));
    (feedback.improvement_suggestions ?? [])
        .filter(({ active, criterion_id }) => {
            if (active === false) return false;
            return (
                criterionIds.has(String(criterion_id)) ||
                (includeGeneral && !mappedCriterionIds.has(String(criterion_id)))
            );
        })
        .forEach((item) => {
            if (item.positive || item.negative) {
                const body = [
                    item.positive?.active !== false && text(item.positive?.text)
                        ? `${t('assignment:feedback-section.positive-title', 'What you did well')}: ${text(item.positive.text)}`
                        : '',
                    item.negative?.active !== false && text(item.negative?.text)
                        ? `${t('assignment:feedback-section.improvement-title', 'How you can improve')}: ${text(item.negative.text)}`
                        : '',
                ].filter(Boolean);
                const criterion = quiz.rubric?.criteria?.find(({ id }) => String(id) === String(item.criterion_id));
                if (body.length) parts.push([text(criterion?.title), ...body].filter(Boolean).join('\n'));
            } else {
                parts.push(text(item.text));
            }
        });
    return parts.filter(Boolean).join('\n\n');
};

export const buildQuestionQuizSubmissionsCSV = (quiz, submissions, accountUsers, t, classSubmissions = submissions) => {
    const overview = getQuizOverview(quiz);
    const criteriaById = new Map((quiz.rubric?.criteria ?? []).map((criterion) => [String(criterion.id), criterion]));
    const questions = overview.timelyQuestions.map((question) => {
        const criterionIds = [...new Set(question.criterionIds.map(String))];
        return { ...question, criterionIds, criteria: criterionIds.map((id) => criteriaById.get(id)) };
    });
    const mappedCriterionIds = new Set(questions.flatMap(({ criterionIds }) => criterionIds));
    const scored = quiz.ai_grading_enabled !== false && !isUnscoredRubric(quiz);
    const classAverage = average(
        classSubmissions.map((submission) => getQuizGradeSnapshot(submission, quiz)?.grade ?? null)
    );
    const questionAverages = questions.map((question) =>
        average(classSubmissions.map((submission) => questionScore(submission, question, false, scored)))
    );
    const criterionAverages = new Map(
        [...mappedCriterionIds].map((id) => [
            id,
            average(
                classSubmissions.map((submission) => criterionScore(submission, criteriaById.get(id), false, scored))
            ),
        ])
    );
    const csv = [columns.map(([key, fallback]) => t(`export.${key}`, fallback))];
    submissions.forEach((submission) => {
        const reviewed = confirmed(submission);
        const generatedSelections = Object.fromEntries(
            Object.entries(submission.ai?.grading ?? {}).map(([id, selection]) => [id, selection?.optionId])
        );
        questions.forEach((question, questionIndex) => {
            const feedback = [
                formatFeedback(
                    submission.ai?.feedback,
                    question,
                    questionIndex === 0,
                    mappedCriterionIds,
                    quiz,
                    generatedSelections,
                    t
                ),
                reviewed
                    ? formatFeedback(
                          submission.grading?.feedback,
                          question,
                          questionIndex === 0,
                          mappedCriterionIds,
                          quiz,
                          submission.grading?.criteria,
                          t
                      )
                    : '',
            ];
            const criteria = question.criteria.length ? question.criteria : [null];
            criteria.forEach((criterion, criterionIndex) => {
                const firstQuestionRow = criterionIndex === 0;
                const firstStudentRow = questionIndex === 0 && firstQuestionRow;
                csv.push(
                    [
                        ...(firstStudentRow
                            ? [
                                  submission.api?.user?.identifier ?? submission.api?.user?.id ?? '',
                                  submission.api?.user?.name ?? '',
                                  getSubmissionSections(submission)
                                      .map(({ name }) => name)
                                      .join(', '),
                                  overview.totalPoints,
                                  classAverage,
                                  getQuizGradeSnapshot(submission, quiz, { suggested: true })?.grade ?? null,
                                  getQuizGradeSnapshot(submission, quiz)?.grade ?? null,
                              ]
                            : Array(7).fill('')),
                        ...(firstQuestionRow
                            ? [
                                  text(question.content),
                                  question.points,
                                  questionAverages[questionIndex],
                                  questionScore(submission, question, true, scored),
                                  questionScore(submission, question, false, scored),
                              ]
                            : Array(5).fill('')),
                        text(criterion?.title),
                        isUnscoredRubric(quiz) ? null : maxPoints(criterion),
                        criterionAverages.get(String(criterion?.id)),
                        criterionScore(submission, criterion, true, scored),
                        criterionScore(submission, criterion, false, scored),
                        ...(firstQuestionRow ? feedback : ['', '']),
                        ...(firstStudentRow && reviewed
                            ? [getSubmissionGradingTime(submission), getSubmissionGradedBy(submission, accountUsers)]
                            : ['', '']),
                    ].map(cell)
                );
            });
        });
    });
    return csv;
};

const criterionRating = (submission, criterion, suggested) => {
    if (!suggested && !confirmed(submission)) return Placeholder.NO_DATA;
    const optionId = suggested
        ? submission.ai?.grading?.[criterion.id]?.optionId
        : submission.grading?.criteria?.[criterion.id];
    if (optionId == null || optionId === '') return Placeholder.NO_DATA;
    return text(criterion.options?.find(({ id }) => String(id) === String(optionId))?.title) || Placeholder.NO_DATA;
};

export const buildHolisticQuizSubmissionsCSV = (quiz, submissions, accountUsers, t, classSubmissions = submissions) => {
    const criteria = quiz.rubric?.criteria ?? [];
    const { totalPoints } = getQuizOverview(quiz);
    const unscored = isUnscoredRubric(quiz);
    const scored = quiz.ai_grading_enabled !== false && !unscored;
    const possible = unscored ? null : sum(criteria.map(maxPoints));
    const classTotal = average(classSubmissions.map((submission) => getQuizRubricScore(submission, quiz)));
    const finalClassAverage = average(
        classSubmissions.map((submission) => getQuizGradeSnapshot(submission, quiz)?.grade ?? null)
    );
    const criterionAverages = criteria.map((criterion) =>
        average(classSubmissions.map((submission) => criterionScore(submission, criterion, false, scored)))
    );
    const context = { criteria, criterionIds: criteria.map(({ id }) => String(id)) };
    const criterionIds = new Set(context.criterionIds);
    const formatHolisticFeedback = (feedback, selections) =>
        formatFeedback(
            feedback?.feedback_type === 'templated' && feedback.commentary
                ? { ...feedback, feedback_type: 'custom' }
                : feedback,
            context,
            true,
            criterionIds,
            quiz,
            selections,
            t
        );
    const value = (input) => cell(input ?? Placeholder.NO_DATA);
    const csv = [holisticColumns.map(([key, fallback]) => t(`export.${key}`, fallback))];
    submissions.forEach((submission) => {
        const reviewed = confirmed(submission);
        const suggestedTotal = getQuizRubricScore(submission, quiz, { suggested: true });
        const selectedTotal = getQuizRubricScore(submission, quiz);
        const student = [
            submission.api?.user?.identifier ?? submission.api?.user?.id ?? '',
            submission.api?.user?.name ?? '',
            getSubmissionSections(submission)
                .map(({ name }) => name)
                .join(', '),
            totalPoints,
            value(finalClassAverage),
            value(getQuizGradeSnapshot(submission, quiz, { suggested: true })?.grade),
            value(getQuizGradeSnapshot(submission, quiz)?.grade),
        ];
        criteria.forEach((criterion, index) => {
            csv.push(
                [
                    ...(index === 0 ? student : Array(7).fill('')),
                    text(criterion.title),
                    value(unscored ? null : maxPoints(criterion)),
                    value(criterionAverages[index]),
                    value(criterionScore(submission, criterion, true, scored)),
                    criterionRating(submission, criterion, true),
                    value(criterionScore(submission, criterion, false, scored)),
                    criterionRating(submission, criterion, false),
                    ...Array(4).fill(''),
                ].map(cell)
            );
        });
        const generatedSelections = Object.fromEntries(
            Object.entries(submission.ai?.grading ?? {}).map(([id, selection]) => [id, selection?.optionId])
        );
        csv.push(
            [
                ...(criteria.length ? Array(7).fill('') : student),
                t('grading.rubric-points', 'Rubric Points'),
                value(possible),
                value(classTotal),
                value(suggestedTotal),
                Placeholder.NO_DATA,
                value(selectedTotal),
                Placeholder.NO_DATA,
                reviewed ? getSubmissionGradingTime(submission) : Placeholder.NO_DATA,
                reviewed ? getSubmissionGradedBy(submission, accountUsers) : Placeholder.NO_DATA,
                formatHolisticFeedback(submission.ai?.feedback, generatedSelections),
                reviewed ? formatHolisticFeedback(submission.grading?.feedback, submission.grading?.criteria) : '',
            ].map(cell)
        );
    });
    return csv;
};

export const exportQuizSubmissionsToCSV = (
    course,
    quiz,
    submissions,
    accountUsers,
    t,
    classSubmissions = submissions
) => {
    const title = (quiz.title || t('create.untitled', 'Untitled Quiz')).replace(/[\\/:*?"<>|]/g, '_');
    const buildCSV =
        quiz.rubric_type === QuizRubricType.QUESTION
            ? buildQuestionQuizSubmissionsCSV
            : buildHolisticQuizSubmissionsCSV;
    return downloadCSV(
        `${course.name}-${title}.csv`.toLowerCase().replaceAll(' ', '_'),
        buildCSV(quiz, submissions, accountUsers, t, classSubmissions)
    );
};
